import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Mail, Inbox, Send, FileText, Trash2, Star, Search, Plus, RefreshCw,
  ChevronLeft, Reply, Forward, X, AlertCircle, Clock, User, Paperclip
} from 'lucide-react';
import api from '../api';
import { toast } from 'sonner';

const IvoryMailPage = () => {
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState({ inbox: 0, unread: 0, sent: 0, drafts: 0, trash: 0 });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFolder, setActiveFolder] = useState('inbox');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCompose, setShowCompose] = useState(false);
  const [composeData, setComposeData] = useState({
    recipientId: '', recipientName: '', recipientEmail: '',
    subject: '', body: '', priority: 'Normal', category: 'General',
    patientId: '', patientName: ''
  });

  const folders = [
    { id: 'inbox', label: 'Inbox', icon: Inbox, count: stats.unread },
    { id: 'sent', label: 'Sent', icon: Send, count: null },
    { id: 'drafts', label: 'Drafts', icon: FileText, count: stats.drafts },
    { id: 'trash', label: 'Trash', icon: Trash2, count: null }
  ];

  const priorities = ['Low', 'Normal', 'High', 'Urgent'];
  const categories = ['General', 'Clinical', 'Administrative', 'Billing', 'Scheduling', 'Compliance'];

  useEffect(() => {
    fetchData();
  }, [activeFolder]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [messagesRes, statsRes, usersRes] = await Promise.all([
        api.get(`/ivorymail/messages?folder=${activeFolder}`),
        api.get('/ivorymail/stats'),
        api.get('/ivorymail/users')
      ]);
      
      if (messagesRes.data.success) setMessages(messagesRes.data.messages || []);
      if (statsRes.data.success) setStats(statsRes.data.stats);
      if (usersRes.data.success) setUsers(usersRes.data.users || []);
    } catch (error) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (isDraft = false) => {
    if (!isDraft && (!composeData.recipientId || !composeData.subject)) {
      toast.error('Please select recipient and enter subject');
      return;
    }
    try {
      await api.post('/ivorymail/messages', { ...composeData, isDraft });
      toast.success(isDraft ? 'Draft saved' : 'Message sent');
      setShowCompose(false);
      setComposeData({ recipientId: '', recipientName: '', recipientEmail: '', subject: '', body: '', priority: 'Normal', category: 'General', patientId: '', patientName: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleDelete = async (messageId) => {
    try {
      await api.delete(`/ivorymail/messages/${messageId}`);
      toast.success('Message moved to trash');
      setSelectedMessage(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const handleStar = async (messageId, isStarred) => {
    try {
      await api.put(`/ivorymail/messages/${messageId}`, { isStarred: !isStarred });
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, isStarred: !isStarred } : m));
    } catch (error) {
      toast.error('Failed to update message');
    }
  };

  const openMessage = async (message) => {
    setSelectedMessage(message);
    if (!message.isRead && activeFolder === 'inbox') {
      try {
        await api.get(`/ivorymail/messages/${message.id}`);
        setMessages(prev => prev.map(m => m.id === message.id ? { ...m, isRead: true } : m));
        setStats(prev => ({ ...prev, unread: Math.max(0, prev.unread - 1) }));
      } catch (error) {}
    }
  };

  const handleReply = () => {
    if (!selectedMessage) return;
    setComposeData({
      recipientId: selectedMessage.senderId,
      recipientName: selectedMessage.senderName,
      recipientEmail: selectedMessage.senderEmail,
      subject: `Re: ${selectedMessage.subject}`,
      body: `\n\n--- Original Message ---\nFrom: ${selectedMessage.senderName}\nDate: ${selectedMessage.createdAt}\n\n${selectedMessage.body}`,
      priority: 'Normal',
      category: selectedMessage.category || 'General',
      patientId: selectedMessage.patientId || '',
      patientName: selectedMessage.patientName || ''
    });
    setShowCompose(true);
  };

  const filteredMessages = messages.filter(m =>
    m.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.senderName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.body?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Urgent': return 'text-red-600';
      case 'High': return 'text-orange-600';
      case 'Low': return 'text-gray-400';
      default: return 'text-gray-600';
    }
  };

  return (
    <DashboardLayout>
      <div className="flex h-[calc(100vh-120px)]" data-testid="ivorymail-page">
        {/* Sidebar */}
        <div className="w-64 bg-white border-r flex flex-col">
          <div className="p-4">
            <Button className="w-full bg-[#f39c12] hover:bg-[#e67e22] text-white" onClick={() => setShowCompose(true)} data-testid="compose-btn">
              <Plus className="w-4 h-4 mr-2" />Compose
            </Button>
          </div>
          <nav className="flex-1 px-2">
            {folders.map(folder => (
              <button
                key={folder.id}
                onClick={() => { setActiveFolder(folder.id); setSelectedMessage(null); }}
                className={`w-full flex items-center justify-between px-4 py-2 rounded-lg mb-1 ${activeFolder === folder.id ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-100 text-gray-700'}`}
                data-testid={`folder-${folder.id}`}
              >
                <div className="flex items-center gap-3">
                  <folder.icon className="w-5 h-5" />
                  <span>{folder.label}</span>
                </div>
                {folder.count > 0 && (
                  <span className="px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full">{folder.count}</span>
                )}
              </button>
            ))}
          </nav>
          <div className="p-4 border-t text-xs text-gray-500">
            <div>Total: {stats.inbox + stats.sent} messages</div>
            <div>{stats.unread} unread</div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col bg-gray-50">
          {/* Header */}
          <div className="bg-white border-b p-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                <Mail className="w-5 h-5 text-[#f39c12]" />
                IvoryMail
              </h1>
              <span className="text-gray-500 capitalize">{activeFolder}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input placeholder="Search messages..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
              </div>
              <Button variant="outline" size="sm" onClick={fetchData}><RefreshCw className="w-4 h-4" /></Button>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 flex overflow-hidden">
            {/* Message List */}
            <div className={`${selectedMessage ? 'w-1/3' : 'w-full'} border-r overflow-y-auto bg-white`}>
              {loading ? (
                <div className="flex items-center justify-center h-32"><RefreshCw className="w-6 h-6 animate-spin text-gray-400" /></div>
              ) : filteredMessages.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Mail className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No messages in {activeFolder}</p>
                </div>
              ) : (
                filteredMessages.map(msg => (
                  <div
                    key={msg.id}
                    onClick={() => openMessage(msg)}
                    className={`p-4 border-b cursor-pointer hover:bg-gray-50 ${selectedMessage?.id === msg.id ? 'bg-blue-50' : ''} ${!msg.isRead && activeFolder === 'inbox' ? 'bg-blue-50/50' : ''}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <button onClick={(e) => { e.stopPropagation(); handleStar(msg.id, msg.isStarred); }}>
                          <Star className={`w-4 h-4 ${msg.isStarred ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                        </button>
                        <div className={`w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-sm font-medium`}>
                          {(activeFolder === 'sent' ? msg.recipientName : msg.senderName)?.charAt(0) || '?'}
                        </div>
                      </div>
                      <span className="text-xs text-gray-400">{new Date(msg.createdAt).toLocaleDateString()}</span>
                    </div>
                    <div className="ml-10 mt-1">
                      <div className={`text-sm ${!msg.isRead && activeFolder === 'inbox' ? 'font-semibold' : ''}`}>
                        {activeFolder === 'sent' ? msg.recipientName : msg.senderName}
                      </div>
                      <div className={`text-sm truncate ${!msg.isRead && activeFolder === 'inbox' ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
                        {msg.subject}
                      </div>
                      <div className="text-xs text-gray-500 truncate">{msg.body?.substring(0, 60)}...</div>
                      <div className="flex items-center gap-2 mt-1">
                        {msg.priority !== 'Normal' && (
                          <span className={`text-xs ${getPriorityColor(msg.priority)}`}>{msg.priority}</span>
                        )}
                        {msg.patientName && (
                          <span className="text-xs bg-gray-100 px-1 rounded">Patient: {msg.patientName}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Message Detail */}
            {selectedMessage && (
              <div className="flex-1 flex flex-col overflow-hidden bg-white">
                <div className="p-4 border-b flex items-center justify-between">
                  <Button variant="ghost" size="sm" onClick={() => setSelectedMessage(null)}>
                    <ChevronLeft className="w-4 h-4 mr-1" />Back
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={handleReply}><Reply className="w-4 h-4 mr-1" />Reply</Button>
                    <Button variant="outline" size="sm" onClick={() => handleDelete(selectedMessage.id)} className="text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-6">
                  <h2 className="text-xl font-semibold mb-4">{selectedMessage.subject}</h2>
                  <div className="flex items-start gap-4 mb-6 pb-4 border-b">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-medium">
                      {selectedMessage.senderName?.charAt(0) || '?'}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium">{selectedMessage.senderName}</div>
                      <div className="text-sm text-gray-500">{selectedMessage.senderEmail}</div>
                      <div className="text-xs text-gray-400 mt-1 flex items-center gap-2">
                        <Clock className="w-3 h-3" />
                        {new Date(selectedMessage.createdAt).toLocaleString()}
                      </div>
                    </div>
                    <div className="text-right text-sm">
                      <div className={getPriorityColor(selectedMessage.priority)}>{selectedMessage.priority} Priority</div>
                      <div className="text-gray-500">{selectedMessage.category}</div>
                    </div>
                  </div>
                  {selectedMessage.patientName && (
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-500" />
                      <span className="text-sm">Related Patient: <strong>{selectedMessage.patientName}</strong></span>
                    </div>
                  )}
                  <div className="prose max-w-none whitespace-pre-wrap">{selectedMessage.body}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Compose Modal */}
        {showCompose && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
              <div className="p-4 border-b flex items-center justify-between">
                <h2 className="text-lg font-semibold">New Message</h2>
                <button onClick={() => setShowCompose(false)}><X className="w-5 h-5" /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">To *</label>
                  <select
                    value={composeData.recipientId}
                    onChange={(e) => {
                      const u = users.find(u => u.id === e.target.value);
                      setComposeData(prev => ({ ...prev, recipientId: e.target.value, recipientName: u?.name || '', recipientEmail: u?.email || '' }));
                    }}
                    className="w-full border rounded-md px-3 py-2"
                    data-testid="recipient-select"
                  >
                    <option value="">Select Recipient</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Subject *</label>
                  <Input value={composeData.subject} onChange={(e) => setComposeData(prev => ({ ...prev, subject: e.target.value }))} placeholder="Enter subject" data-testid="subject-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Priority</label>
                    <select value={composeData.priority} onChange={(e) => setComposeData(prev => ({ ...prev, priority: e.target.value }))} className="w-full border rounded-md px-3 py-2">
                      {priorities.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Category</label>
                    <select value={composeData.category} onChange={(e) => setComposeData(prev => ({ ...prev, category: e.target.value }))} className="w-full border rounded-md px-3 py-2">
                      {categories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Message *</label>
                  <textarea
                    value={composeData.body}
                    onChange={(e) => setComposeData(prev => ({ ...prev, body: e.target.value }))}
                    className="w-full border rounded-md px-3 py-2 min-h-[200px]"
                    placeholder="Type your message..."
                    data-testid="body-textarea"
                  />
                </div>
              </div>
              <div className="p-4 border-t flex justify-between">
                <Button variant="outline" onClick={() => handleSend(true)}>Save Draft</Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowCompose(false)}>Cancel</Button>
                  <Button className="bg-[#f39c12] hover:bg-[#e67e22] text-white" onClick={() => handleSend(false)} data-testid="send-btn">
                    <Send className="w-4 h-4 mr-2" />Send
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default IvoryMailPage;
